import { useEffect, useRef, useState } from 'react';
import { Sparkles, X, ArrowUpRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { suggerisciMit, promoteAzioneToMit } from '../../lib/api';
import { formatSettimanaConRange, isoWeekRange } from '../../lib/weekFormat';

/**
 * Pulsante "Suggerisci MIT" — apre un drawer/modal e chiede al coach
 * (POST /api/coach/suggerisci-mit) di proporre 1-3 MIT da azioni aperte.
 *
 * Props:
 *  - mode: 'giorno' | 'settimana'
 *  - value: data 'YYYY-MM-DD' (modalità giorno) o etichetta '2026-W24' (modalità settimana)
 *  - azioni: array azioni della finestra corrente (aperte e chiuse — viene filtrato fatto=false)
 *  - kr: array key_results disponibili nel contesto (per arricchire lo stato col confidence/scadenza)
 *  - onPromoted: (deletedAzioneId, createdMit) => void  — per optimistic update lato parent
 *  - testid: data-testid del pulsante principale
 */
export default function SuggestMitButton({ mode, value, azioni, kr, onPromoted, testid }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidate, setCandidate] = useState([]);
  const [overrideDate, setOverrideDate] = useState({}); // azione_id -> giorno YYYY-MM-DD
  const [promoting, setPromoting] = useState({}); // azione_id -> bool
  const [errorMsg, setErrorMsg] = useState('');
  const [coachMsg, setCoachMsg] = useState('');
  const drawerRef = useRef(null);

  const openWithFetch = async () => {
    setOpen(true);
    setLoading(true);
    setErrorMsg('');
    setCandidate([]);
    setCoachMsg('');
    try {
      // Filtra azioni aperte e costruisce lo stato compatto per il coach
      const azioniAperte = (azioni || []).filter((a) => !a.fatto).map((a) => ({
        id: a.id,
        testo: a.testo,
        key_result_id: a.key_result_id || null,
        is_bau: Boolean(a.is_bau),
        area: a.area || null,
        settimana: a.settimana || null,
      }));
      const krCompact = (kr || []).map((k) => ({
        id: k.id,
        titolo: k.titolo,
        confidence: k.confidence,
        valore_attuale: k.valore_attuale,
        target: k.target_committed,
        scadenza: k.scadenza,
      }));
      const body = { stato: { azioni: azioniAperte, kr: krCompact } };
      if (mode === 'giorno') body.giorno = value;
      else if (mode === 'settimana') body.settimana = value;
      const res = await suggerisciMit(body);
      const list = res?.candidate || [];
      setCandidate(list);
      setCoachMsg(res?.messaggio || '');
      if (!list.length) {
        setErrorMsg('Nessun suggerimento. Forse non hai abbastanza azioni aperte collegate a KR.');
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || 'Errore chiamata coach');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setCandidate([]);
    setOverrideDate({});
    setErrorMsg('');
    setCoachMsg('');
  };

  // ESC chiude
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const promote = async (c) => {
    if (promoting[c.azione_id]) return;
    setPromoting((p) => ({ ...p, [c.azione_id]: true }));
    const day = overrideDate[c.azione_id] || c.giorno_suggerito;
    // Optimistic: rimuovi la candidata dalla lista localmente prima della risposta
    setCandidate((arr) => arr.filter((x) => x.azione_id !== c.azione_id));
    try {
      const res = await promoteAzioneToMit(c.azione_id, day);
      toast.success(`Promossa a MIT per il ${day}`);
      if (onPromoted) onPromoted(c.azione_id, res?.mit, day);
    } catch (err) {
      // Rollback: riaggiungi la candidata
      setCandidate((arr) => {
        if (arr.find((x) => x.azione_id === c.azione_id)) return arr;
        return [...arr, c].sort((a, b) => (a.giorno_suggerito || '').localeCompare(b.giorno_suggerito || ''));
      });
      const msg = err?.response?.data?.detail || 'Errore promozione';
      toast.error(msg);
    } finally {
      setPromoting((p) => {
        const n = { ...p };
        delete n[c.azione_id];
        return n;
      });
    }
  };

  // Raggruppa candidate per giorno_suggerito (rispetta override locale)
  const grouped = (() => {
    const m = new Map();
    for (const c of candidate) {
      const d = overrideDate[c.azione_id] || c.giorno_suggerito || '—';
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(c);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  // Genera options di date per il selector (settimana → 7 gg, giorno → solo quel gg)
  const dayOptions = (() => {
    if (mode === 'giorno') return [value];
    const r = isoWeekRange(value);
    if (!r) return [];
    const [mon] = r;
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon);
      d.setUTCDate(mon.getUTCDate() + i);
      return d.toISOString().slice(0, 10);
    });
  })();

  return (
    <>
      <button
        type="button"
        onClick={openWithFetch}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink bg-canvas hover:bg-ink hover:text-canvas font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
        data-testid={testid || 'suggerisci-mit-btn'}
        title="Coach suggerisce MIT dalle azioni aperte"
      >
        <Sparkles size={12} strokeWidth={1.5} />
        Suggerisci MIT
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-ink/40 flex justify-end"
          onClick={close}
          data-testid="suggerisci-mit-overlay"
        >
          <div
            ref={drawerRef}
            className="bg-canvas h-full w-full max-w-2xl overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="suggerisci-mit-drawer"
          >
            <div className="sticky top-0 bg-canvas border-b border-line px-6 py-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft">Smart suggest</div>
                <h2 className="font-display text-2xl tracking-tight mt-1">MIT proposte dal coach</h2>
                <p className="font-body text-xs text-mute mt-1">
                  {mode === 'giorno'
                    ? `Per il giorno ${value}`
                    : `Distribuite sulla settimana ${formatSettimanaConRange(value)}`}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 hover:bg-linesoft"
                data-testid="suggerisci-mit-close"
                aria-label="Chiudi"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-6 py-6">
              {loading && (
                <div className="flex items-center gap-3 text-soft" data-testid="suggerisci-mit-loading">
                  <Loader2 size={16} className="animate-spin" />
                  <span className="font-mono text-xs uppercase tracking-[0.15em]">Il coach sta valutando le tue azioni…</span>
                </div>
              )}

              {!loading && errorMsg && (
                <p className="font-body text-sm text-risk mb-4" data-testid="suggerisci-mit-error">{errorMsg}</p>
              )}

              {!loading && coachMsg && (
                <div className="mb-6 p-3 border-l-2 border-accent bg-canvas" data-testid="suggerisci-mit-coach-msg">
                  <p className="font-body text-sm text-ink italic">{coachMsg}</p>
                </div>
              )}

              {!loading && grouped.length > 0 && (
                <div className="space-y-8">
                  {grouped.map(([day, items]) => (
                    <section key={day} data-testid={`suggerisci-mit-group-${day}`}>
                      <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink mb-3 pb-1 border-b border-line">
                        {fmtDayLong(day)}
                      </h3>
                      <ul className="space-y-3">
                        {items.map((c) => {
                          const isBusy = Boolean(promoting[c.azione_id]);
                          return (
                            <li
                              key={c.azione_id}
                              className="p-3 border border-line hover:border-ink transition-colors"
                              data-testid={`suggerisci-mit-row-${c.azione_id}`}
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <p className="font-body text-sm text-ink leading-snug flex-1">{c.testo}</p>
                                <button
                                  type="button"
                                  onClick={() => promote(c)}
                                  disabled={isBusy}
                                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1 bg-ink text-canvas font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-accent disabled:opacity-50"
                                  data-testid={`suggerisci-mit-promote-${c.azione_id}`}
                                >
                                  {isBusy
                                    ? <Loader2 size={10} className="animate-spin" />
                                    : <ArrowUpRight size={10} strokeWidth={1.5} />}
                                  Promuovi a MIT
                                </button>
                              </div>
                              {c.motivo && (
                                <p className="font-body text-xs text-soft italic mb-2">→ {c.motivo}</p>
                              )}
                              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.15em] text-mute">
                                {dayOptions.length > 1 ? (
                                  <label className="inline-flex items-center gap-1">
                                    <span>Giorno:</span>
                                    <select
                                      value={overrideDate[c.azione_id] || c.giorno_suggerito}
                                      onChange={(e) => setOverrideDate((m) => ({ ...m, [c.azione_id]: e.target.value }))}
                                      className="bg-canvas border border-line focus:border-ink outline-none px-1 py-0.5 font-mono text-[10px] tabular-nums"
                                      data-testid={`suggerisci-mit-day-select-${c.azione_id}`}
                                    >
                                      {dayOptions.map((d) => (
                                        <option key={d} value={d}>{fmtDayShort(d)}</option>
                                      ))}
                                    </select>
                                  </label>
                                ) : (
                                  <span>Giorno: {fmtDayShort(c.giorno_suggerito)}</span>
                                )}
                                {c.key_result_id && (
                                  <span className="truncate" title={c.key_result_id}>KR: {c.key_result_id.slice(0, 8)}…</span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              )}

              {!loading && !grouped.length && !errorMsg && (
                <p className="font-body text-sm text-mute italic">Nessun suggerimento.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ───── helpers ────────────────────────────────────────────────────────────
const DAY_LONG_IT = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const MONTH_LONG_IT = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const DAY_SHORT_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTH_SHORT_IT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

function fmtDayLong(iso) {
  if (!iso || iso.length < 10) return iso || '—';
  const d = new Date(iso + 'T00:00:00Z');
  return `${DAY_LONG_IT[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_LONG_IT[d.getUTCMonth()]}`;
}

function fmtDayShort(iso) {
  if (!iso || iso.length < 10) return iso || '—';
  const d = new Date(iso + 'T00:00:00Z');
  return `${DAY_SHORT_IT[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_SHORT_IT[d.getUTCMonth()]}`;
}
